import * as React from 'react';
import { ResourceLoadingState, SelectResourceState } from '../../common/store/stateHelpers';
import ReactTable, { RowInfo, Column, SortingRule } from 'react-table';
import Grid from '@material-ui/core/Grid';
import FilterPanel from './FilterPanel';
import { FilterInfo, FilterSettingsModel } from '../../common/models/CommonModels';
import * as filtersApi from '../../api/filterValuesApi'
import { PagingFilter } from '../../common/models/PagingModels';
import { localizations } from '../../localization/localization';
import TableLoadingIndicator from './TableLoadingIndicator';
import { FilterSettingsState } from '../../account/store/FilterSettingsStore';

const getExpandedStyle = (expanded: boolean) => {
    return {
        border: expanded ? "1px solid #013759" : null,
        backgroundColor: expanded ? "#013759" : null,
        color: expanded ? "white" : null
    }
}

const getPageInputIsInvalid = (value: string, pages: number) => {
    const intValue = parseInt(value, 10);
    return isNaN(intValue) || intValue <= 0 || intValue > pages;
}

interface FilteredTableProps<T> extends ResourceLoadingState<T>, SelectResourceState<T> {
    onFetchData: (filters: FilterInfo, paging: PagingFilter) => void;
    toSubComponent?: (row: RowInfo) => JSX.Element;
    columns: Column[];
    filterIncludes: {
        units: boolean;
        trucks: boolean;
    },
    initialFilterValues?: {
        reportNumberSearch?: string;
    },
    defaultSortedColumns?: SortingRule[];
    refreshSwitch: boolean;
    filterSettings: FilterSettingsState,
    onSaveFilterCancel: () => void;
    onSaveFilterSubmit: (values: FilterSettingsModel) => void;
    updateSaveFilterDestinationCities: (data: string[]) => void;
    updateSaveFilterDepartureCities: (data: string[]) => void;
    updateSaveFilterReporters: (data: string[]) => void;
    onSaveFilter: (data?: FilterSettingsModel, afterAddOrEdit?: () => void) => void;
}

interface Reporter {
    id: string;
    name: string;
}

interface FilteredTableState {
    reporters: Reporter[];
    properties: string[];
    page: number;
    pageSize: number;
    sorted: any[];
    expanded: any;
    pageInputValue: string;
}

type FilterProps<T> = FilteredTableProps<T>;

class FilteredTableWithPaging<T> extends React.Component<FilterProps<T>, FilteredTableState> {

    private cachedFilters: FilterInfo;

    constructor(props: FilterProps<T>) {
        super(props);

        this.state = {
            reporters: [],
            properties: [],
            page: 0,
            pageSize: 10,
            sorted: props.defaultSortedColumns || [],
            expanded: {},
            pageInputValue: '1',
        }

        this.handleFetchData = this.handleFetchData.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.handlePageSizeChange = this.handlePageSizeChange.bind(this);
        this.handleSortedChange = this.handleSortedChange.bind(this);
        this.reloadFilters = this.reloadFilters.bind(this);
    }

    public componentDidMount() {
        this.reloadFilters();
    }

    public componentWillReceiveProps(newProps: FilterProps<T>) {
        const { refreshSwitch } = this.props;

        if (newProps.refreshSwitch !== refreshSwitch) {
            this.reloadFilters();
        }
    }

    public render() {
        const { isLoading, toSubComponent, columns } = this.props;
        const { reporters } = this.state;

        const data = this.props.data;
        const pages = Math.ceil(this.props.data.length / this.state.pageSize);
        const footerContainer = {
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            padding: 3,
            boxShadow: '0 0 15px 0 rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.1)',
            borderTopWidth: 0,
        };
        const buttonContainer = {
            flex: 1,
        };
        const button = {
            color: 'white',
            backgroundColor: '#40A6AE',
            width: '100%',
            height: '100%',
            padding: 6,
            border: 0,
            borderRadius: 3,
            fontSize: '1em',
            outline: 'none',
            cursor: 'pointer',
        };
        const buttonDisabled = {
            ...button,
            opacity: 0.5,
            cursor: 'default',
        };
        const centerContainer = {
            flex: '1.5 1',
            marginBottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            textAlign: 'center',
        } as React.CSSProperties;
        const inputStyle = {
            width: 70,
            border: '1px solid rgba(0,0,0,0.1)',
            background: '#fff',
            padding: '5px 7px',
            borderRadius: 3,
            margin: '3px 10px',
            boxSizing: 'border-box',
            textAlign: 'center',
        } as React.CSSProperties;
        const inputStyleInvalid = {
            ...inputStyle,
            border: '1px solid rgba(255,0,0,0.8)',
            color: 'red',
        };
        const selectStyle = {
            border: '1px solid rgba(0,0,0,0.1)',
            background: '#fff',
            padding: '5px 7px',
            borderRadius: 3,
            outline: 'none',
        };
        const inputContainer = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
        };
        const optionStyle = {
            minHeight: '1.2em',
            padding: '0 2px 1px',
        };

        return (
            <Grid item xs>
                <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <FilterPanel
                            onFiltersChanged={this.handleFetchData(false)}
                            filterValues={{ reporters }}
                            initialValues={this.props.initialFilterValues}
                            filterIncludes={this.props.filterIncludes}
                            onSaveFilter={this.props.onSaveFilter}
                            onSaveFilterCancel={this.props.onSaveFilterCancel}
                            onSaveFilterSubmit={this.props.onSaveFilterSubmit}
                            updateSaveFilterDestinationCities={this.props.updateSaveFilterDestinationCities}
                            updateSaveFilterDepartureCities={this.props.updateSaveFilterDepartureCities}
                            updateNewFilterSettingsReporters={this.props.updateSaveFilterReporters}
                            filterSettings={this.props.filterSettings}
                        />
                    </Grid>
                    <Grid item>
                        <div style={{ backgroundColor: "white" }}>
                            <ReactTable
                                data={data}
                                pages={pages}
                                columns={columns}
                                showPagination={false}
                                defaultPageSize={10}
                                multiSort
                                defaultSorted={this.props.defaultSortedColumns}
                                page={this.state.page}
                                pageSize={this.state.pageSize}
                                sorted={this.state.sorted}
                                onPageChange={this.handlePageChange}
                                onPageSizeChange={this.handlePageSizeChange}
                                onSortedChange={this.handleSortedChange}
                                getTheadTrProps={() => ({ style: { height: "40px" }})}
                                getTrProps={(state: any, rowInfo: RowInfo, column: Column | undefined) => {

                                    const expanded = this.state.expanded[rowInfo.viewIndex] && this.state.expanded[rowInfo.viewIndex] !== false;

                                    return {
                                        style: Object.assign({}, {
                                            textAlign: "center",
                                            height: "52px",
                                            padding: "10px 0",
                                            cursor: "pointer"
                                        },
                                        getExpandedStyle(expanded))
                                    };
                                }}
                                getTdProps={(state: any, rowInfo: RowInfo, column: Column | undefined, instance: any) => {
                                    if (column && !!!column.expander) {
                                        return {
                                            onClick: (e: any) => {
                                                this.setState(prevState => {
                                                    let value = {};
                                                    if (prevState.expanded[rowInfo.viewIndex] && prevState.expanded[rowInfo.viewIndex] !== "false") {
                                                        value = false;
                                                    }

                                                    return { expanded: Object.assign({}, prevState.expanded, { [rowInfo.viewIndex]: value }) }
                                                })
                                            }
                                        }
                                    }
                                    else {
                                        return {}
                                    }
                                }}
                                pageText="Sivu"
                                ofText=" / "
                                rowsText="riviä"
                                nextText={localizations.actions.next}
                                previousText={localizations.actions.previous}
                                expanded={this.state.expanded}
                                loading={isLoading}
                                loadingText={localizations.actions.loading}
                                noDataText=""
                                LoadingComponent={TableLoadingIndicator}
                                minRows={0}
                                className="-highlight"
                                collapseOnDataChange
                                collapseOnSortingChange
                                collapseOnPageChange
                                SubComponent={toSubComponent}
                                onFetchData={this.handleFetchData}
                                onExpandedChange={expanded => this.setState({ expanded })}
                            />
                            {!isLoading ? (
                                <div style={footerContainer}>
                                    <div style={buttonContainer}>
                                        <button
                                            type="button"
                                            style={this.state.page > 0 ? button : buttonDisabled}
                                            disabled={this.state.page <= 0}
                                            onClick={() => {
                                                this.setState({
                                                    page: this.state.page - 1,
                                                    pageInputValue: String(this.state.page),
                                                });
                                            }}
                                        >
                                            {localizations.actions.previous}
                                        </button>
                                    </div>
                                    <div style={centerContainer}>
                                        <span style={inputContainer}>
                                        {localizations.paging.page}
                                            <div>
                                                <input
                                                    type="number"
                                                    onChange={e => {
                                                        const intValue = parseInt(e.target.value, 10);
                                                        if (!isNaN(intValue) && intValue > 0 && intValue <= pages) {
                                                            this.setState({
                                                                page: intValue - 1,
                                                                pageInputValue: e.target.value
                                                            });
                                                            return;
                                                        }
                                                        this.setState({ pageInputValue: e.target.value });
                                                    }}
                                                    style={getPageInputIsInvalid(this.state.pageInputValue, pages) ?
                                                        inputStyleInvalid : inputStyle}
                                                    value={this.state.pageInputValue}
                                                    max={pages}
                                                    min="1"
                                                />
                                            </div>
                                            <span>
                                                / {pages}
                                            </span>
                                        </span>
                                        <span style={{ margin: '3px 10px' }}>
                                            <select
                                                onChange={e => {
                                                    if (!e.target.value || e.target.value === "") {
                                                        return;
                                                    }
                                                    const intValue = parseInt(e.target.value, 10);
                                                    const topRow = this.state.pageSize * this.state.page;
                                                    const newPage = Math.floor(topRow / intValue)
                                                    this.setState({
                                                        pageSize: intValue,
                                                        page: newPage,
                                                        pageInputValue: String(newPage + 1),
                                                    });
                                                }}
                                                value={this.state.pageSize}
                                                style={selectStyle}
                                            >
                                                <option value="5" style={optionStyle}>{localizations.paging.rows.fiveRows}</option>
                                                <option value="10" style={optionStyle}>{localizations.paging.rows.tenRows}</option>
                                                <option value="20" style={optionStyle}>{localizations.paging.rows.twentyRows}</option>
                                                <option value="25" style={optionStyle}>{localizations.paging.rows.twentyFiveRows}</option>
                                                <option value="50" style={optionStyle}>{localizations.paging.rows.fiftyRows}</option>
                                                <option value="100" style={optionStyle}>{localizations.paging.rows.hundredRows}</option>
                                            </select>
                                        </span>
                                    </div>
                                    <div style={buttonContainer}>
                                        <button
                                            type="button"
                                            style={this.state.page < pages - 1 ? button : buttonDisabled}
                                            disabled={this.state.page >= pages - 1}
                                            onClick={() => {
                                                this.setState({
                                                    page: this.state.page + 1,
                                                    pageInputValue: String(this.state.page + 2),
                                                });
                                            }}
                                        >
                                            Seuraava
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    private reloadFilters() {
        filtersApi.getReporters(this.props.filterIncludes.trucks, this.props.filterIncludes.units).then(data => {
            this.setState({
                reporters: data
            });
        });
    }

    private handleFetchData = (invokedByTable: boolean) => (filters: FilterInfo) => {

        if (invokedByTable) {
            this.props.onFetchData(this.cachedFilters, { currentPage: this.state.page, rowsPerPage: this.state.pageSize });
        }
        else {
            this.setState({
                expanded: {}
            });

            this.cachedFilters = filters;
            this.props.onFetchData(filters, { currentPage: this.state.page, rowsPerPage: this.state.pageSize });
        }
    }

    private handlePageChange(page: any) {
        this.setState({
            page
        });
    }

    private handlePageSizeChange(pageSize: any, page: any) {
        this.setState({
            pageSize,
            page
        });

        this.handleFetchData(true);
    }

    private handleSortedChange(sorted: any) {
        this.setState({
            sorted,
            expanded: {}
        });

        this.handleFetchData(true);
    }
}

export default FilteredTableWithPaging;


// WEBPACK FOOTER //
// ./src/components/tables/FilteredTableWithPaging.tsx